import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedRole, setUserRoles } from "../../../store/slices/authSlice";
import { Toast } from "../../../utils/SwalUti";
import { get, put } from "../../../utils/fetchAPI";
import "./chambers-settings.scss";

/**
 * Settings → Chambers tab.
 *
 * A "chamber" is a UserRole (organization + office location the user practices at).
 * Two distinct concepts are managed here:
 *   - status (active / inactive): whether the chamber is enabled. Inactive chambers are
 *     hidden from the navbar switcher and login (the server filters status: "active").
 *   - current chamber (selectedRole): the one the user is operating in right now.
 *
 * Unlike the navbar (which only knows the active chambers from login), this tab fetches the
 * FULL list via `GET user-role/chambers/:userId` so inactive chambers can be shown and
 * reactivated. Status/sequence changes go through `PUT user-role/chamber/update/:id`;
 * afterwards Redux (`userRoles` + `selectedRole`) is re-synced so the navbar stays consistent.
 */
const ChambersSettings = () => {
  const dispatch = useDispatch();
  const { user, userRoles, selectedRole } = useSelector((state) => state.auth);
  const userId = user?.user?._id;

  const fallback = Array.isArray(userRoles) ? userRoles : [];
  const [chambers, setChambers] = useState(fallback);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [seqDraft, setSeqDraft] = useState({});

  // Keep the navbar / persisted state in sync with the server truth after any change.
  const syncReduxFromList = (list) => {
    const active = list
      .filter((c) => c?.status === "active")
      .sort((a, b) => (a?.sequence ?? 1) - (b?.sequence ?? 1));
    dispatch(setUserRoles(active));
    if (selectedRole?._id) {
      const stillActive = active.find((c) => c._id === selectedRole._id);
      dispatch(setSelectedRole(stillActive || active[0] || null));
    } else if (active[0]) {
      dispatch(setSelectedRole(active[0]));
    }
  };

  const loadChambers = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await get(`user-role/chambers/${userId}`);
      if (res?.status === 200 && Array.isArray(res.data)) {
        setChambers(res.data);
        syncReduxFromList(res.data);
      }
    } catch (e) {
      // Network/permission error — keep whatever we already have (Redux fallback).
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadChambers();
  }, [userId]);

  const toggleStatus = async (chamber) => {
    const next = chamber?.status === "active" ? "inactive" : "active";
    setBusyId(chamber._id);
    try {
      const res = await put(`user-role/chamber/update/${chamber._id}`, { status: next });
      if (res?.status === 200) {
        await loadChambers();
        Toast.fire({ icon: "", title: next === "active" ? "Chamber activated" : "Chamber deactivated" });
      }
    } catch (e) {
      Toast.fire({ icon: "error", title: "Could not update chamber" });
    } finally {
      setBusyId(null);
    }
  };

  const commitSequence = async (chamber, value) => {
    const seq = Number(value);
    const current = chamber?.sequence ?? 1;
    const clearDraft = () =>
      setSeqDraft((d) => {
        const next = { ...d };
        delete next[chamber._id];
        return next;
      });

    if (!Number.isFinite(seq) || seq < 1 || seq === current) {
      clearDraft();
      return;
    }
    setBusyId(chamber._id);
    try {
      const res = await put(`user-role/chamber/update/${chamber._id}`, { sequence: seq });
      if (res?.status === 200) {
        clearDraft();
        await loadChambers();
        Toast.fire({ icon: "", title: "Order updated" });
      }
    } catch (e) {
      clearDraft();
      Toast.fire({ icon: "error", title: "Could not update order" });
    } finally {
      setBusyId(null);
    }
  };

  const setAsCurrent = (chamber) => {
    if (!chamber?._id || chamber._id === selectedRole?._id) return;
    dispatch(setSelectedRole(chamber));
    Toast.fire({ icon: "", title: "Switched current chamber" });
  };

  const roles = Array.isArray(chambers) ? chambers : [];

  return (
    <div className="basic-info profile-panel chambers-panel">
      <div className="profile-panel__toolbar">
        <div>
          <h2 className="profile-panel__title">Chambers</h2>
          <p className="profile-panel__subtitle">
            The chambers you practice at. <strong>Active</strong> chambers appear in the navbar
            switcher; deactivate one to hide it. <strong>Order</strong> sets how they are listed,
            and <strong>Current</strong> is the chamber in use now.
          </p>
        </div>
      </div>

      {loading && roles.length === 0 ? (
        <p className="chambers-empty">Loading chambers…</p>
      ) : roles.length === 0 ? (
        <p className="chambers-empty">No chambers are linked to your account yet.</p>
      ) : (
        <div className="chambers-list">
          {roles.map((chamber, index) => {
            const isCurrent = !!chamber?._id && chamber._id === selectedRole?._id;
            const isActive = chamber?.status === "active";
            const busy = busyId === chamber?._id;
            const orgName = chamber?.location?.organization?.name;
            // One speciality per role; admin-assigned via the master list.
            const specialityName = chamber?.specialty?.[0]?.display;
            const seqValue = seqDraft[chamber?._id] ?? (chamber?.sequence ?? 1);

            return (
              <div
                key={chamber?._id || index}
                className={
                  "chamber-row" +
                  (isCurrent ? " chamber-row--current" : "") +
                  (isActive ? "" : " chamber-row--inactive")
                }
              >
                <div className="chamber-row__head">
                  <div className="chamber-row__info">
                    <span className="chamber-row__name">
                      {chamber?.location?.name || "Unnamed chamber"}
                    </span>
                    {orgName && <span className="chamber-row__org">{orgName}</span>}
                    {specialityName && (
                      <span className="chamber-row__specialty">{specialityName}</span>
                    )}
                  </div>
                  <span className={`chamber-status chamber-status--${isActive ? "active" : "inactive"}`}>
                    {isActive ? "Active" : "Inactive"}
                  </span>
                </div>

                <div className="chamber-row__actions">
                  <label className="chamber-seq">
                    <span className="chamber-seq__label">Order</span>
                    <input
                      type="number"
                      min={1}
                      className="chamber-seq__input"
                      value={seqValue}
                      disabled={busy}
                      onChange={(e) =>
                        setSeqDraft((d) => ({ ...d, [chamber._id]: e.target.value }))
                      }
                      onBlur={(e) => commitSequence(chamber, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.target.blur();
                      }}
                    />
                  </label>

                  <button
                    type="button"
                    className={`chamber-btn chamber-btn--${isActive ? "deactivate" : "activate"}`}
                    onClick={() => toggleStatus(chamber)}
                    disabled={busy}
                  >
                    {isActive ? "Deactivate" : "Activate"}
                  </button>
                  <button
                    type="button"
                    className="chamber-btn chamber-btn--current"
                    onClick={() => setAsCurrent(chamber)}
                    disabled={busy || isCurrent || !isActive}
                    title={!isActive ? "Activate this chamber first" : undefined}
                  >
                    {isCurrent ? "Current" : "Set as current"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ChambersSettings;
